/**
 * AuthConfig shape validator — enforces canonical authConfig artifact shape
 * against generated `auth-config.json` artifacts at artifact-write time.
 *
 * Same contract-drift bug class as swp-l6yu (Pulse content schema validator)
 * and swp-fimq (api auth.type literal validator). The auth-otter has emitted
 * `authConfig.method: oidc` plus a nested `authConfig.oidc: { discoveryUrl,
 * clientId, clientSecret, ... }` block for runs 5-8/8 of the
 * disaster-health-logistics project. The canonical AuthConfig schema
 * (a discriminated union on `type`) requires flat `authConfig.type: oidc`
 * with all provider fields hoisted to the root of `authConfig`.
 *
 * The auth-otter self-flags this in its own thinking output before shipping
 * the wrong shape anyway — the retryPrompt mechanism is the right gate.
 *
 * Wired into pipeline.ts _validateArtifactInner for phase: 'auth' at step 4.5,
 * BEFORE the PHASE_ZOD_VALIDATORS call so the otter gets an actionable prompt
 * instead of a generic Zod discriminator error.
 *
 * Refs: swp-with (P1.2, follow-on from swp-fimq 3f26f02b)
 */

// ---------------------------------------------------------------------------
// Nested provider block detection
//
// The canonical authConfigSchema is a discriminated union on 'type' across
// two variants: pkiConfigSchema and oidcConfigSchema. ALL provider-specific
// fields are hoisted to the root of authConfig — no strategy-named sub-objects.
//
// Schema scope: authConfigSchema covers 'oidc' and 'pki'.
// TODO(swp-with-follow): If the schema is extended to 'oauth2', 'basic',
// or 'apiKey' variants, add those strategy names and example fields here.
// ---------------------------------------------------------------------------

const KNOWN_NESTED_STRATEGIES = ['oidc', 'pki'] as const;
type NestedStrategy = (typeof KNOWN_NESTED_STRATEGIES)[number];

/** Example field names for each strategy — used in before/after retryPrompt. */
const NESTED_STRATEGY_FIELD_EXAMPLES: Record<NestedStrategy, [string, string, string]> = {
  oidc: ['discoveryUrl', 'clientId', 'clientSecret'],
  pki: ['profile', 'source', 'headerPrefix'],
};

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface AuthConfigValidationResult {
  valid: boolean;
  retryPrompt?: string;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** Format the nested-block violation error for a given strategy. */
function nestedBlockError(
  strategy: NestedStrategy,
  nestedObj: Record<string, unknown>,
  artifactPath: string
): string {
  const exampleFields = NESTED_STRATEGY_FIELD_EXAMPLES[strategy];
  // Show up to 2 actual keys from the nested block so the otter sees its own output.
  const actualKeys = Object.keys(nestedObj).slice(0, 2);
  const showField0 = actualKeys[0] ?? exampleFields[0];
  const showField1 = actualKeys[1] ?? exampleFields[1];

  return [
    `${artifactPath}: \`authConfig.${strategy}\` must not be a nested provider block.`,
    `Hoist \`authConfig.${strategy}.*\` fields to \`authConfig.*\`. The canonical AuthConfig`,
    `schema does not nest provider-specific fields under a named sub-object.`,
    ``,
    `Bad (nested block):`,
    `  "authConfig": {`,
    `    "type": "${strategy}",`,
    `    "${strategy}": {`,
    `      "${showField0}": "...",`,
    `      "${showField1}": "...",`,
    `      ...`,
    `    }`,
    `  }`,
    ``,
    `Correct (hoisted to root of authConfig):`,
    `  "authConfig": {`,
    `    "type": "${strategy}",`,
    `    "${exampleFields[0]}": "...",`,
    `    "${exampleFields[1]}": "...",`,
    `    "${exampleFields[2]}": "...",`,
    `    ...`,
    `  }`,
    ``,
    `Move every key from \`authConfig.${strategy}\` up to \`authConfig\` and remove the \`"${strategy}"\` sub-object.`,
  ].join('\n');
}

/** Format the discriminator-key violation error. */
function discriminatorKeyError(methodVal: string, artifactPath: string): string {
  return [
    `${artifactPath}: \`authConfig.method\` is not a valid key in the canonical AuthConfig schema.`,
    `Rename \`authConfig.method\` → \`authConfig.type\`. The canonical AuthConfig schema uses`,
    `\`type\` as the auth-strategy discriminator, matching the api-config \`auth.type\` convention`,
    `shipped in swp-fimq.`,
    ``,
    `Bad:`,
    `  "authConfig": { "method": "${methodVal}", ... }`,
    ``,
    `Correct:`,
    `  "authConfig": { "type": "${methodVal}", ... }`,
  ].join('\n');
}

/** Format the ambiguous-discriminator error (both method AND type present). */
function ambiguousDiscriminatorError(
  methodVal: string,
  typeVal: string,
  artifactPath: string
): string {
  return [
    `${artifactPath}: \`authConfig\` has both \`method: "${methodVal}"\` and \`type: "${typeVal}"\` — ambiguous discriminator.`,
    `Remove \`authConfig.method\` and keep only \`authConfig.type\`. The canonical AuthConfig`,
    `schema uses \`type\` as the sole auth-strategy discriminator.`,
  ].join('\n');
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Validate an auth-config artifact's authConfig block against the canonical
 * AuthConfig shape.
 *
 * Catches two distinct violation classes:
 *
 *   **Violation 1 — wrong discriminator key:**
 *   `authConfig.method` present. Canonical is `authConfig.type`. This is the
 *   literal field name the auth-otter emits when it mirrors the
 *   stackwright.auth.yml file format (which uses `method:`) into the artifact
 *   JSON (which should use `type:`).
 *
 *   **Violation 2 — nested provider block:**
 *   `authConfig.oidc: { ... }` or `authConfig.pki: { ... }` present. The
 *   canonical shape hoists those fields to the root of `authConfig`.
 *
 * Edge cases:
 *   - No `authConfig` key → `valid: true` (devOnly bypass / no-auth project)
 *   - Both `method` AND `type` present → reject as ambiguous discriminator
 *   - Flat `type: oidc` with hoisted fields → `valid: true` (canonical happy path)
 *   - Non-object `authConfig` → `valid: true` (Zod will catch shape issues)
 *
 * @param artifact     The parsed artifact JSON. Accepts `unknown` so callers
 *                     don't need to cast.
 * @param artifactPath Relative artifact path for retryPrompt context
 *                     (e.g. ".stackwright/artifacts/auth-config.json").
 * @returns { valid, retryPrompt? }
 */
export function validateAuthConfig(
  artifact: unknown,
  artifactPath: string
): AuthConfigValidationResult {
  if (artifact === null || typeof artifact !== 'object') {
    return { valid: true }; // not an object — structural validator will catch it
  }

  const obj = artifact as Record<string, unknown>;

  // No authConfig → devOnly bypass / no-auth project → skip validation
  if (!('authConfig' in obj) || obj['authConfig'] === undefined || obj['authConfig'] === null) {
    return { valid: true };
  }

  if (typeof obj['authConfig'] !== 'object' || Array.isArray(obj['authConfig'])) {
    return { valid: true }; // non-object authConfig shape — Zod handles this
  }

  const authConfig = obj['authConfig'] as Record<string, unknown>;
  const errors: string[] = [];

  // --- Violation 1: discriminator key drift ---

  const hasMethod = 'method' in authConfig && authConfig['method'] !== undefined;
  const hasType = 'type' in authConfig && authConfig['type'] !== undefined;

  if (hasMethod && hasType) {
    // Belt-and-suspenders: both keys present — ambiguous
    errors.push(
      ambiguousDiscriminatorError(
        String(authConfig['method']),
        String(authConfig['type']),
        artifactPath
      )
    );
  } else if (hasMethod && !hasType) {
    // Pure violation: method present, type absent
    errors.push(discriminatorKeyError(String(authConfig['method']), artifactPath));
  }
  // hasType && !hasMethod → correct discriminator → no error

  // --- Violation 2: nested provider block ---

  for (const strategy of KNOWN_NESTED_STRATEGIES) {
    const nested = authConfig[strategy];
    if (
      nested !== null &&
      nested !== undefined &&
      typeof nested === 'object' &&
      !Array.isArray(nested)
    ) {
      errors.push(nestedBlockError(strategy, nested as Record<string, unknown>, artifactPath));
    }
  }

  if (errors.length === 0) return { valid: true };

  const retryPrompt = [
    `Auth config validation failed: ${errors.length} violation(s) in ${artifactPath}.`,
    ``,
    `You MUST fix ALL violations and re-call stackwright_pro_validate_artifact.`,
    ``,
    ...errors.flatMap((e) => [e, '']),
    `The canonical authConfig shape uses \`type\` (not \`method\`) as the discriminator,`,
    `with all provider-specific fields hoisted to the root of \`authConfig\`.`,
    `AuthConfig is a discriminated union on \`type: 'oidc' | 'pki'\`.`,
  ].join('\n');

  return { valid: false, retryPrompt };
}
